import dynamic from 'next/dynamic'
import { Link } from '@/navigation'
import { FaLinkedinIn, FaTelegramPlane, FaYoutube } from 'react-icons/fa'

import Logo from '../Logo'
import RoutesContainer from './RoutesContainer'

const ScrollToTopButton = dynamic(() => import('../ScrollToTopButton'), { ssr: false })

import type { FC } from 'react'
import type { IPreferencesData, IServicesData, IServicesTransformedFooterData } from '@/types'

const Nav: FC<{ data: IPreferencesData[]; services: IServicesData[] }> = ({ data, services }) => {
  const transformedServices: IServicesTransformedFooterData[] = services.reduce(
    (acc: IServicesTransformedFooterData[], currentItem: IServicesData) => {
      const existingCategory = acc.find(
        (item) => item.category?.id === currentItem.category_parent?.id
      )

      if (existingCategory) {
        existingCategory.sections.push({
          category: currentItem.category_parent,
          category_child: currentItem.category,
        })
      } else {
        acc.push({
          title: currentItem.category_parent?.name,
          category: currentItem.category_parent,
          sections: [
            {
              category: currentItem.category_parent,
              category_child: currentItem.category,
            },
          ],
        })
      }

      return acc
    },
    []
  )

  const removeDuplicates = (sections: any) => {
    const seenIds = new Set() // To track seen category_child.id values
    return sections.filter((section: any) => {
      const categoryId = section.category_child.id
      if (!seenIds.has(categoryId)) {
        seenIds.add(categoryId)
        return true // Include the first occurrence
      }
      return false // Exclude duplicates
    })
  }

  const filteredServices = transformedServices.map((item) => ({
    ...item,
    sections: removeDuplicates(item.sections),
  }))

  const socialsLogos: { [key: string]: React.ReactElement } = {
    telegram: <FaTelegramPlane />,
    linkedin: <FaLinkedinIn />,
    youtube: <FaYoutube />,
  }

  const socials = data
    .filter((item) => item.key in socialsLogos)
    .map((item) => ({
      name: `${item.key}-footer`,
      path: item.value,
      logo: socialsLogos[item.key],
    }))

  const keyOfAddress: any = {
    email: 'mailto:',
    phone: 'tel:',
    address: '',
  }

  const address = data
    .filter((item) => item.key in keyOfAddress)
    .map((item) => ({
      name: item.value,
      path: keyOfAddress[item.key] + item.value,
    }))

  return (
    <section className="container relative mx-auto py-10">
      <ScrollToTopButton />
      <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        <div>
          <Link href="/" aria-label="logo image">
            <Logo color="white" />
          </Link>
          <div className="mt-4 flex flex-row gap-6 text-secondary-darkest">
            {socials.map(({ name, path, logo }, i) => (
              <a
                href={path}
                key={name + i}
                aria-label={name}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="rounded-full bg-white p-1">{logo}</div>
              </a>
            ))}
          </div>
        </div>
        <RoutesContainer address={address} filteredServices={filteredServices} />
      </div>
    </section>
  )
}

export default Nav
